import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAudiencesList, refreshAudience, archiveAudience, exportAudienceCsv } from '../config/api';
import { toast } from 'react-toastify';
import useIsMobile from '../hooks/useIsMobile';

const Audiences = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // all | static | dynamic
  const [statusFilter, setStatusFilter] = useState('active'); // active | archived

  // Refresh confirmation modal state
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [refreshDiff, setRefreshDiff] = useState(null);
  const [refreshTargetId, setRefreshTargetId] = useState(null);

  useEffect(() => {
    fetchAudiences();
  }, [activeTab, statusFilter]);

  const fetchAudiences = async () => {
    setLoading(true);
    try {
      const res = await getAudiencesList({
        search: searchTerm,
        type: activeTab === 'all' ? undefined : activeTab,
        status: statusFilter,
      });
      if (res && res.data && Array.isArray(res.data.audiences)) {
        setAudiences(res.data.audiences);
      } else {
        setAudiences([]);
      }
    } catch (err) {
      setAudiences([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyId = (formattedId, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(formattedId);
    toast.success(`Copied ${formattedId} to clipboard!`);
  };

  const handleRefresh = async (audId, e) => {
    e.stopPropagation();
    try {
      toast.info(`Refreshing snapshot for AUD-${audId}...`);
      await refreshAudience(audId);
      toast.success(`Audience snapshot refreshed successfully!`);
      fetchAudiences();
    } catch (err) {
      if (err.requires_confirmation) {
        setRefreshTargetId(audId);
        setRefreshDiff(err.responseData);
        setShowRefreshModal(true);
      } else {
        toast.error(err.message || 'Failed to refresh audience');
      }
    }
  };

  const handleConfirmRefresh = async () => {
    setShowRefreshModal(false);
    if (!refreshTargetId) return;
    try {
      toast.info('Applying refresh...');
      await refreshAudience(refreshTargetId, { confirm: true });
      toast.success('Audience snapshot refreshed successfully!');
      fetchAudiences();
    } catch (err) {
      toast.error(err.message || 'Failed to refresh audience');
    } finally {
      setRefreshDiff(null);
      setRefreshTargetId(null);
    }
  };

  const handleArchive = async (audId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to archive this audience?')) return;
    try {
      await archiveAudience(audId);
      toast.success('Audience archived');
      fetchAudiences();
    } catch (err) {
      if (err.status === 400) {
        toast.error(err.message || 'Cannot archive: audience is currently in use.', { autoClose: 8000 });
      } else {
        toast.error(err.message || 'Failed to archive audience');
      }
    }
  };

  const handleTemplateClick = (templateKey) => {
    navigate(`/audiences/create?template=${templateKey}`);
  };

  const handleExportCsv = async (audId, e) => {
    e.stopPropagation();
    try {
      await exportAudienceCsv(audId);
    } catch (err) {
      toast.error(err.message || 'Failed to export CSV');
    }
  };

  const filteredAudiences = audiences.filter((aud) => {
    const matchesSearch =
      !searchTerm ||
      aud.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (aud.formattedId || `AUD-${aud.id}`).toLowerCase().includes(searchTerm.toLowerCase()) ||
      aud.description_auto.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = activeTab === 'all' || aud.type === activeTab;
    const matchesStatus = (aud.status || 'active') === statusFilter;

    return matchesSearch && matchesType && matchesStatus;
  });

  return (
    <div className="container-fluid" style={{ padding: '24px', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      
      {/* Header Banner */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>Audience</span>
            <span style={{ fontSize: '12px', background: '#e0f2fe', color: '#0369a1', padding: '4px 10px', borderRadius: '12px', fontWeight: '600' }}>
              Reusable Targeting Engine
            </span>
          </h2>
          <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px', margin: 0 }}>
            Create reusable user segments for Push Notifications, WhatsApp, SMS, Coupons, and Promocash.
          </p>
        </div>

        <Link
          to="/audiences/create"
          className="btn btn-primary"
          style={{
            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '14px',
            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span>+ Create New Audience</span>
        </Link>
      </div>

      {/* Main Filter & Listing Card */}
      <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        
        {/* Controls Bar */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', background: '#fafafa' }}>
          
          {/* Tabs */}
          <div style={{ display: 'flex', gap: '6px', background: '#e2e8f0', padding: '3px', borderRadius: '8px' }}>
            {[
              { id: 'all', label: 'All Types' },
              { id: 'static', label: 'Static (Snapshots / A/B)' },
              { id: 'dynamic', label: 'Dynamic (Live)' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  border: 'none',
                  background: activeTab === tab.id ? '#ffffff' : 'transparent',
                  color: activeTab === tab.id ? '#0f172a' : '#64748b',
                  padding: '6px 14px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: activeTab === tab.id ? '600' : '500',
                  cursor: 'pointer',
                  boxShadow: activeTab === tab.id ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search & Status Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="text"
              placeholder="Search by name, ID (e.g. AUD-104), or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '8px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                width: '100%',
                maxWidth: '320px',
                outline: 'none',
              }}
            />

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                backgroundColor: '#ffffff',
                color: '#334155',
              }}
            >
              <option value="active">Active Audiences</option>
              <option value="archived">Archived Audiences</option>
            </select>
          </div>
        </div>

        {/* Table List */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', color: '#334155' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase', fontSize: '11px', color: '#64748b', letterSpacing: '0.5px' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', width: '120px' }}>Audience ID</th>
                <th style={{ padding: '12px 16px', textAlign: 'left' }}>Audience Name & Plain-English Filter</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', width: '110px' }}>Type</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', width: '130px' }}>Members</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', width: '160px' }}>A/B Split Groups</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', width: '120px' }}>Usage Log</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', width: '200px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    Loading audiences...
                  </td>
                </tr>
              ) : filteredAudiences.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    No audiences found matching your search filters.
                  </td>
                </tr>
              ) : (
                filteredAudiences.map((aud) => {
                  const formattedId = aud.formattedId || `AUD-${aud.id}`;
                  return (
                    <tr
                      key={aud.id}
                      style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.15s ease' }}
                      onClick={() => navigate(`/audiences/${aud.id}`)}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = '#ffffff')}
                    >
                      {/* ID Chip */}
                      <td style={{ padding: '16px' }}>
                        <span
                          onClick={(e) => handleCopyId(formattedId, e)}
                          title="Click to copy ID"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: '#f1f5f9',
                            border: '1px solid #cbd5e1',
                            color: '#1e293b',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor: 'pointer',
                          }}
                        >
                          {formattedId}
                        </span>
                      </td>

                      {/* Name & Auto-Summary */}
                      <td style={{ padding: '16px' }}>
                        <div style={{ fontWeight: '600', color: '#0f172a', fontSize: '14px' }}>
                          {aud.name}
                        </div>
                        <div style={{ color: '#64748b', fontSize: '12px', marginTop: '2px', lineHeight: '1.4' }}>
                          {aud.description_auto}
                        </div>
                      </td>

                      {/* Type Badge */}
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <span
                          style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            background: aud.type === 'static' ? '#e0f2fe' : '#fef3c7',
                            color: aud.type === 'static' ? '#0369a1' : '#b45309',
                          }}
                        >
                          {aud.type === 'static' ? 'Static' : 'Dynamic'}
                        </span>
                      </td>

                      {/* Member Count */}
                      <td style={{ padding: '16px', textAlign: 'right' }}>
                        <span style={{ fontWeight: '700', fontSize: '14px', color: '#0f172a' }}>
                          {aud.member_count?.toLocaleString() || 0}
                        </span>
                        <span style={{ fontSize: '11px', color: '#64748b', display: 'block' }}>users</span>
                      </td>

                      {/* A/B Split Groups */}
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        {aud.has_split || (aud.child_groups && aud.child_groups.length > 0) ? (
                          <div>
                            <span style={{ background: '#dcfce7', color: '#15803d', padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>
                              A/B Split Active
                            </span>
                             <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>
                              {aud.split_pct ? `${aud.split_pct}% Treatment / ${100 - aud.split_pct}% Holdout` : 'Split Active'}
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '12px' }}>Single Group</span>
                        )}
                      </td>

                      {/* Usage Count */}
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <span style={{ background: '#f1f5f9', color: '#334155', padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                          {aud.usage_count ? `${aud.usage_count} uses` : 'Unused'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td style={{ padding: '16px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                          <Link
                            to={`/audiences/${aud.id}`}
                            className="btn btn-sm btn-outline-primary"
                            style={{ fontSize: '12px', padding: '4px 10px' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            View
                          </Link>

                          {aud.type === 'static' && (
                            <button
                              onClick={(e) => handleRefresh(aud.id, e)}
                              className="btn btn-sm btn-outline-secondary"
                              style={{ fontSize: '12px', padding: '4px 8px' }}
                              title="Re-run filter snapshot"
                            >
                              Refresh
                            </button>
                          )}

                          <button
                            onClick={(e) => handleExportCsv(aud.id, e)}
                            className="btn btn-sm btn-outline-success"
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                            title="Export Members CSV"
                          >
                            CSV
                          </button>

                          <button
                            onClick={(e) => handleArchive(aud.id, e)}
                            className="btn btn-sm btn-outline-danger"
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                            title="Archive Audience"
                          >
                            Archive
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Refresh Diff Confirmation Modal */}
      {showRefreshModal && refreshDiff && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#ffffff', borderRadius: '12px', padding: '28px', width: '500px', maxWidth: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ background: '#fef3c7', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                🔄
              </div>
              <h4 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
                Confirm Refresh
              </h4>
            </div>
            <p style={{ fontSize: '14px', color: '#475569', marginBottom: '16px', lineHeight: '1.5' }}>
              Refreshing this audience will significantly change its membership:
            </p>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '12px', textAlign: 'center' }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Previous</div>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>
                    {(refreshDiff.previous_count || 0).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>New Count</div>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: '#2563eb' }}>
                    {(refreshDiff.new_count || 0).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Change</div>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: (refreshDiff.diff || 0) >= 0 ? '#16a34a' : '#dc2626' }}>
                    {(refreshDiff.diff || 0) >= 0 ? '+' : ''}{(refreshDiff.diff || 0).toLocaleString()}
                  </div>
                </div>
              </div>
              {refreshDiff.change_pct != null && (
                <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '13px', color: '#64748b' }}>
                  ({refreshDiff.change_pct}% change in membership)
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => { setShowRefreshModal(false); setRefreshDiff(null); setRefreshTargetId(null); }}
                className="btn btn-outline-secondary"
                style={{ borderRadius: '8px', padding: '8px 16px', fontWeight: '600', fontSize: '13px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRefresh}
                className="btn btn-primary"
                style={{ borderRadius: '8px', padding: '8px 16px', fontWeight: '600', fontSize: '13px' }}
              >
                Yes, Apply Refresh
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Audiences;
